import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Container,
  Nav,
  Row,
  Tab,
} from '@valid-eval/shared-react-components';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Loading from 'components/Loading';
import { loadMultiple as loadEvents } from 'data/actions/events';
import { load as loadUser } from 'data/actions/users';
import {
  getCurrentUser,
  getCurrentUserActiveEvents,
  getCurrentUserArchivedEvents,
  getCurrentUserDraftEvents,
  isLoadingEvents,
} from 'data/reducers';
import Search from 'images/icons/search.png';
import withRouter from 'routes/withRouter';

import { openReportURL } from 'screens/components/MainMenu/helpers';
import DashboardEventsGrid from './components/DashboardEventsGrid';
import DashboardTabButton from './components/DashboardTabButton';
import Styles from './Dashboard.module.scss';

const getValue = (item, field) => {
  if (field === 'name') return item.get('name').toLowerCase();
  return new Date(item.get(field));
};

const sortEvents = (orderBy, sortOrder) => (a, b) => {
  const aValue = getValue(a, orderBy);
  const bValue = getValue(b, orderBy);

  let comparison;
  if (orderBy === 'name') comparison = aValue.localeCompare(bValue);
  else comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;

  return sortOrder === 'asc' ? comparison : -comparison;
};

const Dashboard = ({
  loadEvents,
  user,
  loadUser,
  loading,
  archivedEvents,
  activeEvents,
  draftEvents,
  location,
  navigate,
}) => {
  const [key, setKey] = useState('activeCohorts');
  const [eventFilter, setEventFilter] = useState('');
  const [userLoaded, setUserLoaded] = useState(false);

  const [orderBy, setOrderBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const loadData = async () => {
      // Reload user events
      await loadUser(user.get('id'));
      await loadEvents();
      setUserLoaded(true);
    };

    loadData();
  }, []);

  const search = (name) => {
    setEventFilter(name.toLowerCase());
  };

  useEffect(() => {
    if (activeEvents.size === 0 && archivedEvents.size > 0) setKey('archivedCohorts');
    if (activeEvents.size) setKey('activeCohorts');
  }, [activeEvents, archivedEvents]);

  const filteredEventsActive = activeEvents
    .filter((item) => item.get('name').toLowerCase().includes(eventFilter))
    .sort(sortEvents(orderBy, sortOrder));

  const filteredEventsArchived = archivedEvents
    .filter((item) => item.get('name').toLowerCase().includes(eventFilter))
    .sort(sortEvents(orderBy, sortOrder));

  const filteredEventsDraft = draftEvents
    .filter((item) => item.get('name').toLowerCase().includes(eventFilter))
    .sort(sortEvents(orderBy, sortOrder));

  const orderByHandler = (order) => {
    setOrderBy(order);
    if (orderBy === order) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    const downloadParam = location.state?.download;
    if (userLoaded && downloadParam) {
      openReportURL(downloadParam, false, () => {});
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, userLoaded, navigate, location.pathname]);

  return (
    <Container>
      <Tab.Container activeKey={key}>
        <Row className={cx('m-0 mt-3 px-4')}>
          <Col xs={12}>
            <h1 className={Styles.titleDashboard}>Dashboard</h1>
          </Col>
        </Row>
        <Row className="mx-0 px-4 justify-content-between">
          <Col xs="auto">
            <Nav activeKey={key} onSelect={setKey} as={ButtonGroup}>
              <DashboardTabButton
                active={key === 'activeCohorts'}
                label="Active Cohorts"
                badge={filteredEventsActive.size}
                eventKey="activeCohorts"
              />
              <DashboardTabButton
                active={key === 'archivedCohorts'}
                label="Archived Cohorts"
                badge={filteredEventsArchived.size}
                eventKey="archivedCohorts"
              />
              {draftEvents.size > 0 && (
                <DashboardTabButton
                  active={key === 'draftCohorts'}
                  label="Draft Cohorts"
                  badge={filteredEventsDraft.size}
                  eventKey="draftCohorts"
                />
              )}
            </Nav>
          </Col>
          <Col xs="auto" className="d-flex align-items-center justify-content-md-end">
            <Button
              className="px-1 py-0 text-xs"
              variant="unstyled"
              onClick={() => orderByHandler('name')}
            >
              <span className="fa-stack fa-lg">
                <i
                  className={cx('fa-solid fa-stack-2x', {
                    'fa-arrow-down-a-z':
                      (sortOrder === 'asc' && orderBy === 'name') || orderBy !== 'name',
                    'fa-arrow-up-a-z': sortOrder === 'desc' && orderBy === 'name',
                    'text-gray02': orderBy !== 'name',
                    'text-blue02': orderBy === 'name',
                  })}
                />
              </span>
            </Button>
            <Button
              className="px-1 py-0 text-xs"
              variant="unstyled"
              onClick={() => orderByHandler('created_at')}
            >
              <span className="fa-stack fa-lg">
                <i
                  className={cx('fa-solid  fa-stack-2x', {
                    'fa-calendar-arrow-down':
                      (sortOrder === 'asc' && orderBy === 'created_at') || orderBy !== 'created_at',
                    'fa-calendar-arrow-up': sortOrder === 'desc' && orderBy === 'created_at',
                    'text-gray02': orderBy !== 'created_at',
                    'text-blue02': orderBy === 'created_at',
                  })}
                />
              </span>
            </Button>

            <div className="position-relative">
              <input
                autoFocus
                className={cx('form-control', Styles.SearchInput)}
                placeholder={'Search by Cohort Name'}
                onChange={(e) => search(e.target.value)}
              />
              <img className={Styles.SearchInputIcon} src={Search} alt={'Search by Cohort Name'} />
            </div>
          </Col>
        </Row>
        <Row className={cx('mx-0 px-4')}>
          <Col xs={12}>
            {loading ? (
              <Loading />
            ) : (
              <Tab.Content>
                <Tab.Pane eventKey="activeCohorts">
                  {filteredEventsActive?.size > 0 && (
                    <DashboardEventsGrid user={user} events={filteredEventsActive} />
                  )}
                  {activeEvents?.size > 0 && filteredEventsActive?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      No cohorts found matching your search.
                    </Alert>
                  )}
                  {activeEvents?.size === 0 && filteredEventsActive?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      You presently have no active cohorts assigned to your user profile. Please
                      check the "Notifications" drop down in the upper right-hand corner of this
                      view for any invitations.
                    </Alert>
                  )}
                </Tab.Pane>
                <Tab.Pane eventKey="archivedCohorts">
                  {filteredEventsArchived?.size > 0 && (
                    <DashboardEventsGrid user={user} events={filteredEventsArchived} />
                  )}
                  {archivedEvents?.size > 0 && filteredEventsArchived?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      No cohorts found matching your search.
                    </Alert>
                  )}
                  {archivedEvents?.size === 0 && filteredEventsArchived?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      You presently have no archived cohorts assigned to your user profile.
                    </Alert>
                  )}
                </Tab.Pane>
                <Tab.Pane eventKey="draftCohorts">
                  {filteredEventsDraft?.size > 0 && (
                    <DashboardEventsGrid user={user} events={filteredEventsDraft} />
                  )}
                  {draftEvents?.size > 0 && filteredEventsDraft?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      No cohorts found matching your search.
                    </Alert>
                  )}
                  {draftEvents?.size === 0 && filteredEventsDraft?.size === 0 && (
                    <Alert variant="info" className="my-4">
                      You presently have no draft cohorts assigned to your user profile.
                    </Alert>
                  )}
                </Tab.Pane>
              </Tab.Content>
            )}
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

Dashboard.displayName = 'Dashboard';

export default withRouter(
  connect(
    (state) => {
      const user = getCurrentUser(state);
      return {
        user,
        activeEvents: getCurrentUserActiveEvents(state),
        archivedEvents: getCurrentUserArchivedEvents(state),
        draftEvents: getCurrentUserDraftEvents(state),
        loading: isLoadingEvents(state),
      };
    },
    { loadEvents, loadUser },
  )(Dashboard),
);
